/**
 * useAgentChat — hook for the new /api/v2/agent endpoint with SSE streaming.
 *
 * Parses the SSE event stream emitted by the multi-agent pipeline and
 * keeps the calling component updated with:
 *   • messages         — chat-style history (user + assistant)
 *   • reasoningSteps   — live agent thinking for the current turn
 *   • sources          — RAG / web hits as they arrive
 *   • followUps        — generated only after the final_result event
 *   • isStreaming      — true between request start and [DONE] / abort
 *   • currentDecision  — last classifier/route decision payload
 *
 * The hook is intentionally additive — it does NOT touch ChatContext or
 * the legacy /api/chat flow, so dropping it into a side panel or a new
 * page is safe.
 *
 * Usage:
 *   const { sendMessage, messages, reasoningSteps, isStreaming, ... } = useAgentChat();
 *   sendMessage("ما هي أحكام نظام العمل؟", { overrideTask: "search" });
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { useLanguage } from '@/contexts/LanguageContext';
import { getBackendBaseUrl } from '@/lib/backendUrl';
import apiFetch from '@/lib/apiFetch';
import useEffectiveLanguage from '@/hooks/useEffectiveLanguage';

const BACKEND_URL = getBackendBaseUrl();
const API_URL = `${BACKEND_URL}/api/v2/agent`;

const genId = () => `agent_${Date.now()}_${Math.random().toString(36).slice(2, 9)}`;

// Jul 2026: shared buffers for token-stream flushing. Keyed by
// `ans:{assistantId}` / `scribe:{assistantId}`. A setState per token
// saturated the React main thread and froze the renderer during
// generation; deltas are now accumulated here and flushed on a ~120ms
// timer (≈8 paints/sec — smooth to the eye, negligible CPU).
const TOKEN_FLUSH_BUFFERS = new Map();

export const useAgentChat = () => {
  const { session } = useAuth();
  const { language } = useLanguage();
  const getEffectiveLanguage = useEffectiveLanguage();

  const [messages, setMessages] = useState([]); // [{id, role, content, meta}]
  const [reasoningSteps, setReasoningSteps] = useState([]);
  const [sources, setSources] = useState([]);
  const [followUps, setFollowUps] = useState([]);
  const [isStreaming, setIsStreaming] = useState(false);
  const [currentDecision, setCurrentDecision] = useState(null);
  const [error, setError] = useState(null);
  const [sessionId, setSessionId] = useState(null);
  // Sprint 2 — Sequencer fan-out state for the parallel-agent UI.
  // Shape:
  //   { active: boolean,
  //     agents: { examiner: {status, elapsed_s, summary},
  //               regis:    {status, elapsed_s, summary},
  //               counsel:  {status, elapsed_s, summary} },
  //     conductor: {status, elapsed_s} }
  // status ∈ { 'idle','running','completed','failed','waiting' }
  const [sequencerState, setSequencerState] = useState(null);

  const abortRef = useRef(null);
  const assistantIdRef = useRef(null);

  // Cluster B-3.d: write-through active_session_id to the user
  // profile whenever the active session changes. Mirrors the
  // active_chat_id write-through in useChatLogic. The server stamps
  // `active_session_at`; the client enforces a 24h freshness window
  // when consuming the restore-hint on relogin.
  //
  // Guard: only POST when sessionId is a real id. Empty / null
  // would clobber a valid value written from another device.
  useEffect(() => {
    if (!session?.access_token) return;
    if (!sessionId) return;
    let cancelled = false;
    (async () => {
      try {
        await apiFetch(`${BACKEND_URL}/api/auth/profile/sync`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ active_session_id: sessionId }),
        });
      } catch (err) {
        if (!cancelled) console.warn('[active-session-sync] failed', err?.message || err);
      }
    })();
    return () => { cancelled = true; };
  }, [sessionId, session?.access_token]);

  // Send a message and stream the response (with polling fallback for
  // ingress-proxy SSE drops). See top-of-file docstring for the
  // envelope shape.
  const reset = useCallback(() => {
    if (abortRef.current) abortRef.current.abort();
    abortRef.current = null;
    assistantIdRef.current = null;
    setMessages([]);
    setReasoningSteps([]);
    setSources([]);
    setFollowUps([]);
    setIsStreaming(false);
    setCurrentDecision(null);
    setError(null);
    setSessionId(null);
    setSequencerState(null);
  }, []);

  const stop = useCallback(() => {
    if (abortRef.current) abortRef.current.abort();
    setIsStreaming(false);
  }, []);

  // Synthesize an assistant message from a previously-persisted envelope
  // (returned by GET /api/v2/agent/sessions/{id}). Used by the
  // "Recent runs" sidebar to let users re-open historical drafts.
  const loadEnvelope = useCallback((envelope) => {
    if (!envelope || typeof envelope !== 'object') return;
    const result = envelope.result || {};
    const output = result.output || {};
    const text = output.draft_text || output.review_text || output.answer
      || (typeof output === 'string' ? output : '');
    const sid = envelope.session_id;
    const decision = envelope.decision || {};

    const userPreview = output.draft_text
      ? (output.contract_type || decision.task || '')
      : '';

    const assistantId = genId();
    assistantIdRef.current = assistantId;
    const restoredMessages = [];
    if (userPreview) {
      restoredMessages.push({
        id: genId(),
        role: 'user',
        content: userPreview,
      });
    }
    restoredMessages.push({
      id: assistantId,
      role: 'assistant',
      content: text,
      isStreaming: false,
      meta: {
        agent: decision.agent,
        task: decision.task,
        sources: result.sources || [],
        follow_ups: envelope.follow_ups || [],
        output,
        restored: true,
      },
    });

    setMessages(restoredMessages);
    setReasoningSteps(
      (result.reasoning_steps || []).map((step, idx) => ({
        type: 'reasoning_step',
        content: typeof step === 'string' ? step : (step?.step || ''),
        agent: decision.agent,
        timestamp: idx,
      })),
    );
    setSources(result.sources || []);
    setFollowUps(envelope.follow_ups || []);
    setCurrentDecision(decision);
    if (sid) setSessionId(sid);
    setIsStreaming(false);
    setError(null);
  }, []);

  // Cluster B-3.d: consume the restore-hint published by AuthContext
  // on SIGNED_IN. The hint is written exactly once per login (or
  // explicit re-login through ReauthBanner) and is one-shot — we
  // clear it after consuming. Honors the 24h freshness window.
  useEffect(() => {
    if (!session?.access_token) return;
    if (sessionId) return; // user already has an active session — don't override
    if (typeof window === 'undefined') return;
    const hint = window.__BAYENAH_RESTORE_ACTIVE_SESSION;
    if (!hint?.sessionId) return;
    const ageMs = hint.atMs ? (Date.now() - hint.atMs) : Infinity;
    if (ageMs > 24 * 60 * 60 * 1000) {
      try { window.__BAYENAH_RESTORE_ACTIVE_SESSION = null; } catch { /* ignore */ }
      return;
    }
    // Fetch the envelope and let loadEnvelope() rebuild the in-memory state.
    let cancelled = false;
    (async () => {
      try {
        const r = await apiFetch(`${API_URL}/sessions/${hint.sessionId}`, { method: 'GET' });
        if (!r.ok) return;
        const env = await r.json();
        if (!cancelled && env && (env.envelope || env.partial_draft || env.results)) {
          loadEnvelope(env);
          setSessionId(hint.sessionId);
        }
      } catch (err) {
        if (!cancelled) console.warn('[active-session-restore] envelope fetch failed', err?.message || err);
      } finally {
        try { window.__BAYENAH_RESTORE_ACTIVE_SESSION = null; } catch { /* ignore */ }
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [session?.access_token, loadEnvelope]);

  // Send a message and stream the response (with polling fallback for
  // proxy-induced SSE drops — Emergent ingress closes long streams ~60s).
  const sendMessage = useCallback(async (
    message,
    {
      overrideTask = null,
      documentText = null,
      contractType = null,
      extra = null,
      userPreview = null,
    } = {},
  ) => {
    if (!message?.trim()) return;

    setError(null);
    setReasoningSteps([]);
    setSources([]);
    setFollowUps([]);
    setCurrentDecision(null);
    setSequencerState(null);

    // Cluster B.1 (Feb 06 2026): when the caller is an editor action
    // chip (Redraft / Improve / Compliance / Review), the LLM-facing
    // `message` is the detailed prompt that the classifier can route,
    // while the UI-facing placeholder ("Redrafting the document…") is
    // passed via `userPreview`. If absent, fall back to the message
    // itself — preserves the normal Q&A path.
    const userVisible = (userPreview && userPreview.trim()) || message;

    const userMsg = {
      id: genId(),
      role: 'user',
      content: userVisible,
      meta: {
        // Persist the document context on the user message so downstream
        // UI elements (e.g. the "Run Compliance Check" CTA in a failed-
        // Regis bubble) can retry against the same document without the
        // user re-uploading or re-typing.
        documentText: documentText || null,
        contractType: contractType || null,
        extra: extra || null,
        overrideTask: overrideTask || null,
        // Keep the actual prompt around in case we ever need to
        // re-issue without the placeholder.
        llmPrompt: message,
      },
    };
    const assistantId = genId();
    assistantIdRef.current = assistantId;
    const assistantMsg = {
      id: assistantId,
      role: 'assistant',
      content: '',
      meta: { agent: null, task: null, reasoning_steps: [], sources: [], follow_ups: [] },
      isStreaming: true,
    };

    setMessages((prev) => [...prev, userMsg, assistantMsg]);
    setIsStreaming(true);

    const controller = new AbortController();
    abortRef.current = controller;
    let finalResultReceived = false;
    let activeSessionId = sessionId;

    try {
      // Cluster B-3.a: route the live drafting POST through apiFetch
      // so a connect-time 401 surfaces `ReauthBanner` (the user has
      // been signed out between mount and submit) and is replayed
      // after re-auth. The Authorization header is added by
      // apiFetch from the current session — no manual injection.
      const headers = { 'Content-Type': 'application/json' };

      // Cluster C-5 (iter90 hook lift, Feb 06 2026): replaced the
      // inline `resolveLanguage(message, language)` from iter88 with
      // the shared `useEffectiveLanguage` hook. Single call site for
      // every send path; bare `language` cannot be passed by accident
      // because `effectiveLang` is the only value in scope here.
      const effectiveLang = getEffectiveLanguage(message);
      const response = await apiFetch(API_URL, {
        method: 'POST',
        headers,
        signal: controller.signal,
        body: JSON.stringify({
          message,
          language: effectiveLang,
          session_id: sessionId,
          document_text: documentText,
          contract_type: contractType,
          override_task: overrideTask,
          streaming: true,
          extra,
        }),
      });

      if (!response.ok) {
        // Try to parse a JSON error body (FastAPI uses {"detail":"..."}).
        let detail = '';
        let raw = '';
        try {
          raw = await response.text();
          const parsed = JSON.parse(raw);
          detail = parsed?.detail || '';
        } catch { /* raw text */ }

        // HTTP 400 from /api/v2/agent fires only when the backend's
        // binary-bytes guard catches raw DOCX/ZIP bytes in document_text.
        // Surface a localized, user-actionable message so the user knows
        // to upload as PDF or paste text — instead of the bare HTTP code.
        if (response.status === 400) {
          const isAr = (language || '').toLowerCase().startsWith('ar');
          const friendly = detail || (isAr
            ? 'تعذر قراءة الملف المرفق. يرجى تحميله بصيغة PDF أو نسخ النص مباشرة في المحادثة.'
            : 'Could not read the attached file. Please upload as PDF or paste the contract text directly into the chat.');
          throw new Error(friendly);
        }

        if (response.status === 429) {
          const isAr = (language || '').toLowerCase().startsWith('ar');
          throw new Error(detail || (isAr
            ? 'تم تجاوز حد الاستخدام. يرجى المحاولة لاحقاً.'
            : 'Rate limit exceeded. Please try again later.'));
        }

        throw new Error(detail || raw || `HTTP ${response.status}`);
      }

      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let buffer = '';
      const streamStartedAt = Date.now();

      while (true) {
        const { value, done } = await reader.read();
        if (done) break;
        // Sprint 11.2 (2026-06-13) — preview-safe SSE drop simulator.
        // Set `window.__BAYENAH_SIM_SSE_DROP_MS = 12000` in DevTools
        // before submitting a message to abort THIS reader 12 s in.
        // The backend request keeps running normally; only our local
        // reader is severed, which exercises the same code path that
        // fires when Cloudflare cuts a long stream at ~100 s in prod.
        if (typeof window !== 'undefined') {
          const simAt = Number(window.__BAYENAH_SIM_SSE_DROP_MS);
          if (Number.isFinite(simAt) && simAt > 0 && (Date.now() - streamStartedAt) >= simAt) {
            try { window.__BAYENAH_SIM_SSE_DROP_MS = null; } catch { /* ignore */ }
            throw new Error('SSE_SIM_DROP');
          }
        }
        buffer += decoder.decode(value, { stream: true });

        const lines = buffer.split('\n');
        buffer = lines.pop() || '';

        for (const line of lines) {
          if (!line.startsWith('data: ')) continue;
          const data = line.slice(6).trim();
          if (!data) continue;
          if (data === '[DONE]') {
            // handled by reader done
            continue;
          }
          try {
            const event = JSON.parse(data);
            if (event?.type === 'final_result') {
              finalResultReceived = true;
            }
            if (event?.type === 'stream_start') {
              const sid = event.data?.session_id;
              if (sid) activeSessionId = sid;
            }
            handleEvent(event, assistantId, setMessages, setReasoningSteps, setSources, setFollowUps, setCurrentDecision, setSessionId, setSequencerState);
          } catch (e) {
            console.warn('[useAgentChat] failed to parse SSE frame:', e, data);
          }
        }
      }
    } catch (e) {
      if (e.name === 'AbortError') {
        // Sep 2026 (no-silent-failures) — the previous "leave partial
        // state, no error surface" branch left the assistant bubble
        // stuck on '...' forever after the user hit Stop. Every agent
        // run must end with a rendered message; render a neutral,
        // single-language "cancelled" line in the UI language.
        const isArabic = language === 'ar';
        const cancelledMsg = isArabic
          ? 'أوقفت المراجعة قبل اكتمالها.'
          : 'You stopped the review before it finished.';
        setMessages((prev) => prev.map((m) =>
          m.id === assistantId
            ? {
                ...m,
                isStreaming: false,
                content: m.content?.trim() ? m.content : cancelledMsg,
                meta: { ...(m.meta || {}), cancelled: true },
              }
            : m,
        ));
      } else if (!finalResultReceived && activeSessionId) {
        // Sprint 11.2 (2026-06-13) — SSE TRANSPORT DISCONNECT before the
        // backend reached a terminal state. Cloudflare cuts long streams
        // around ~100 s while reviews can run 300 s+; the backend keeps
        // running and persists its envelope when done.
        //
        // We MUST NOT:
        //   • mark the message as `isError: true`     (would render red)
        //   • overwrite `content` with 'Stream failed' (would erase any
        //                                              streamed prose)
        //   • flip sequencer agents to `failed`        (handled by the
        //                                              polling block —
        //                                              agents stay in
        //                                              `running` here
        //                                              until envelope
        //                                              actually settles)
        //
        // Instead we add one neutral reasoning step ("reconnecting")
        // and let the polling fallback below take over. The polling
        // response IS the source of truth from this point on.
        console.warn('[useAgentChat] SSE transport disconnect (pre-terminal) — switching to polling:', e?.message || e);
        const isArabic = language === 'ar';
        const reconMsg = isArabic
          ? 'انقطع الاتصال المباشر — جارٍ متابعة المراجعة في الخلفية...'
          : 'Live connection dropped — reconnecting to the running review...';
        setReasoningSteps((prev) => [...prev, {
          type: 'reasoning_step', content: reconMsg, agent: 'system', timestamp: Date.now() / 1000,
        }]);
        setMessages((prev) => prev.map((m) =>
          m.id === assistantId
            ? { ...m, meta: { ...(m.meta || {}), transportState: 'reconnecting_polling' } }
            : m,
        ));
      } else {
        // Terminal error path: either the POST itself never started
        // streaming (HTTP non-2xx before any SSE frame), or final_result
        // already arrived — i.e. the catch is a real failure, not a
        // mid-flight transport drop. Surface to the user.
        console.error('[useAgentChat] error:', e);
        setError(e.message || String(e));
        setMessages((prev) => prev.map((m) =>
          m.id === assistantId
            ? { ...m, isStreaming: false, isError: true, content: m.content || e.message || 'Stream failed' }
            : m,
        ));
      }
    } finally {
      // ── Polling fallback: if SSE finished without delivering final_result
      // (e.g. proxy closed the stream at 60s while gpt-5.5 still drafted),
      // fetch the persisted envelope from the backend.
      if (!finalResultReceived && activeSessionId && !controller.signal.aborted) {
        // User-facing progress copy — never expose proxy / timeout
        // language. Keep the reasoning panel alive so the user sees
        // that work is still in flight.
        const isArabic = language === 'ar';
        const finalizingMsg = isArabic
          ? 'جارٍ إتمام المراجعة...'
          : 'Finalizing your review...';
        setReasoningSteps((prev) => [...prev, {
          type: 'reasoning_step',
          content: finalizingMsg,
          agent: 'system',
          timestamp: Date.now() / 1000,
        }]);

        try {
          // Sprint 11.2 (2026-06-13) — operator-directed polling discipline:
          //   • Poll every 3 s.
          //   • Run for AT LEAST 360 s regardless of state (a fresh
          //     full_review can run 300 s+ on Examiner alone before the
          //     envelope materialises). The session row may not exist
          //     in agent_sessions/compliance_jobs until orchestrator
          //     persists, so steady 404s are normal early on — do not
          //     interpret them as failure.
          //   • Polling response is the SOURCE OF TRUTH for final
          //     status and per-agent statuses from this point on.
          //   • Tolerate transient 5xx (keep polling); only bail on
          //     401/403 auth-terminal.
          //
          // Stall / hard-cap still apply BEYOND the 360 s floor so
          // the polling loop can't run forever if a session truly
          // disappears.
          const POLL_INTERVAL_MS    = 3 * 1000;       // 3 s
          const MIN_POLL_FLOOR_MS   = 360 * 1000;     // 6 min hard floor
          const STALL_TIMEOUT_MS    = 3 * 60 * 1000;  // 3 min idle (only after floor)
          const HARD_CAP_MS         = 15 * 60 * 1000; // 15 min absolute ceiling
          const pollStartedAt = Date.now();
          let lastProgressAt = Date.now();
          let lastErr = null;
          let pollIdx = 0;
          // Live Narration v3: seq of the last journal event replayed
          // through handleEvent — polling replays only what's new.
          let lastReplayedSeq = -1;
          while (true) {
            const now = Date.now();
            const sincePollStart = now - pollStartedAt;
            const sinceProgress  = now - lastProgressAt;
            // Hard ceiling — defence in depth, never run forever.
            if (sincePollStart >= HARD_CAP_MS) break;
            // After the floor expires, allow the idle stall to terminate
            // the loop too.
            if (sincePollStart >= MIN_POLL_FLOOR_MS && sinceProgress >= STALL_TIMEOUT_MS) break;

            await new Promise((r) => setTimeout(r, POLL_INTERVAL_MS));
            if (controller.signal.aborted) break;
            pollIdx += 1;
            // Append a neutral "still working" tick roughly every 15 s
            // (5 polls × 3 s) so the reasoning panel shows fresh activity.
            if (pollIdx % 5 === 0) {
              setReasoningSteps((prev) => [...prev, {
                type: 'reasoning_step',
                content: finalizingMsg,
                agent: 'system',
                timestamp: Date.now() / 1000,
              }]);
            }
            try {
              const res = await apiFetch(`${API_URL}/sessions/${activeSessionId}`, {});
              if (res.status === 200) {
                const env = await res.json();
                // `status: 'running'` (Sprint 11) — server is alive and
                // the run is in flight. Optionally render partial draft.
                if (env && env.status === 'running') {
                  lastProgressAt = Date.now();
                  // Live Narration v3: the backend journals progress
                  // events to the session row (plan, plan_update,
                  // agent lifecycle, reasoning steps). Replay anything
                  // new through the SAME reducer the SSE path uses —
                  // the checklist and narration stay alive after the
                  // proxy kills the stream at ~60s.
                  if (Array.isArray(env.live_events) && env.live_events.length) {
                    for (const ev of env.live_events) {
                      const seq = typeof ev.seq === 'number' ? ev.seq : -1;
                      if (seq <= lastReplayedSeq) continue;
                      lastReplayedSeq = seq;
                      try {
                        handleEvent(
                          { type: ev.type, timestamp: ev.timestamp, data: ev.data || {} },
                          assistantId,
                          setMessages, setReasoningSteps, setSources, setFollowUps, setCurrentDecision, setSessionId,
                          setSequencerState,
                        );
                      } catch (_re) { /* one bad journal row must not kill polling */ }
                    }
                  }
                  if (env.partial_draft != null) {
                    setSequencerState?.((prev) => prev ? {
                      ...prev,
                      scribe_draft_text: env.partial_draft || '',
                      scribe_word_count: env.partial_word_count || ((env.partial_draft || '').split(/\s+/).filter(Boolean).length),
                    } : prev);
                  }
                  // Sprint 11.4 (2026-06-14) — when the backend signals
                  // `_canonical_pending` it means a sibling (today only
                  // compliance_jobs) reports the run as in-flight while
                  // agent_sessions hasn't materialised yet. Surface a
                  // neutral "still finalizing review" tick every 30 s so
                  // the user can tell the system hasn't frozen. We do
                  // NOT touch sequencerState here — Examiner/Counsel/
                  // Conductor stay in `running` until the canonical
                  // envelope arrives.
                  if (env._canonical_pending && pollIdx % 10 === 0) {
                    const isArabic = language === 'ar';
                    setReasoningSteps((prev) => [...prev, {
                      type: 'reasoning_step',
                      content: isArabic
                        ? 'لا تزال المراجعة قيد الانتهاء... (في انتظار البيان الكامل)'
                        : 'Still finalizing review… (waiting for full envelope)',
                      agent: 'system',
                      timestamp: Date.now() / 1000,
                    }]);
                  }
                  continue;
                }
                // Sprint 11.4 (2026-06-14) — additional defence: even if
                // the backend forgot to set status='running', a sibling-
                // sourced envelope (e.g. compliance_jobs returned in the
                // old Sprint 11 P0 shape) is NEVER terminal on the v2
                // route. Treat any response carrying `_sibling_source`
                // or `_canonical_pending` as still-running and keep
                // polling regardless of the `status` field. The
                // canonical agent_sessions envelope is what we wait for.
                if (env && (env._canonical_pending === true || env._sibling_source)) {
                  lastProgressAt = Date.now();
                  continue;
                }
                // Sprint 11.4 (2026-06-14) — third defence: a terminal-
                // looking response whose envelope shape doesn't carry
                // the expected per-agent success keys for the running
                // mode is, by definition, the wrong sibling. We saw
                // exactly this with the Regis-only compliance_jobs
                // envelope before the backend fix — `result.output`
                // lacked every `*_success` field. Don't paint red on
                // absence; keep polling for the canonical envelope.
                try {
                  const out = (env && env.result && env.result.output) || {};
                  const hasFullReviewKeys = (
                    out.examiner_success !== undefined ||
                    out.counsel_success  !== undefined ||
                    out.conductor_success !== undefined
                  );
                  const hasFullDraftKeys = out.mode === 'full_draft' && out.scribe_success !== undefined;
                  // gap_analysis returns a Regis-only result.output that
                  // INTENTIONALLY lacks examiner/counsel/conductor — but
                  // the polling endpoint only reaches this branch for
                  // gap_analysis via the canonical agent_sessions row,
                  // which the orchestrator stamps with `out.task ===
                  // 'gap_analysis'`. Allow that shape through.
                  const isPureGapAnalysis = (
                    out.task === 'gap_analysis' ||
                    (env.decision && env.decision.task === 'gap_analysis')
                  );
                  if (!hasFullReviewKeys && !hasFullDraftKeys && !isPureGapAnalysis) {
                    lastProgressAt = Date.now();
                    continue;
                  }
                } catch (_e) { /* shape probe failed — fall through and finalize */ }
                // Terminal — envelope present (success or backend-acknowledged failure).
                handleEvent(
                  { type: 'final_result', timestamp: Date.now() / 1000, data: env },
                  assistantId,
                  setMessages, setReasoningSteps, setSources, setFollowUps, setCurrentDecision, setSessionId,
                  // Live Narration v3 fix: this call omitted
                  // setSequencerState, so plan closure (F5) never fired
                  // on the polling-fallback path — the frozen-checklist
                  // bug seen in production on 10 Aug.
                  setSequencerState,
                );
                // Polling-fallback path: the live SSE `conductor_complete`
                // event likely never reached us (proxy cut the stream
                // before the synthesis finished). Reconstruct the
                // sequencer-completed state from the envelope so the
                // SequencerPanel keeps showing its final ✓ row instead
                // of vanishing the moment streaming flips to false.
                // Shape must match `sequencer_start` — agents are nested
                // under `state.agents.*`, conductor is at top level.
                try {
                  const out = (env && env.result && env.result.output) || {};
                  // Sprint 11.4 (2026-06-14) — map ONLY the success keys
                  // that are explicitly present in the envelope. A
                  // missing key means "this agent wasn't part of the
                  // mode" (e.g. counsel on full_draft) or "the writer
                  // didn't include it" — NEVER "this agent failed".
                  // Boolean(undefined) returning false was the
                  // red-screen-on-success bug; now we treat undefined
                  // as "leave the prior sequencer entry alone".
                  const isFullDraft = out.mode === 'full_draft';
                  const has = (k) => out[k] !== undefined;
                  const ok  = (k) => out[k] === true;
                  const mapStatus = (k) => has(k) ? (ok(k) ? 'completed' : 'failed') : null;
                  const examinerStatus  = mapStatus('examiner_success');
                  const regisStatus     = mapStatus('regis_success');
                  const counselStatus   = mapStatus('counsel_success');
                  const scribeStatus    = mapStatus('scribe_success');
                  const conductorStatus = mapStatus('conductor_success');
                  setSequencerState((prev) => prev ? {
                    ...prev,
                    agents: {
                      ...(prev.agents || {}),
                      ...(isFullDraft && scribeStatus
                        ? { scribe: { ...((prev.agents || {}).scribe || {}), status: scribeStatus } }
                        : {}),
                      ...(examinerStatus
                        ? { examiner: { ...((prev.agents || {}).examiner || {}), status: examinerStatus } }
                        : {}),
                      ...(regisStatus
                        ? { regis: { ...((prev.agents || {}).regis || {}), status: regisStatus } }
                        : {}),
                      ...(!isFullDraft && counselStatus
                        ? { counsel: { ...((prev.agents || {}).counsel || {}), status: counselStatus } }
                        : {}),
                    },
                    ...(conductorStatus
                      ? { conductor: { ...(prev.conductor || {}), status: conductorStatus } }
                      : {}),
                    // For full_draft, the draft itself is in the
                    // envelope. Stash it so the right pane's Draft
                    // tab renders even when SSE dropped before
                    // `scribe_complete` arrived.
                    ...(isFullDraft && out.draft_text ? {
                      scribe_draft_text: out.draft_text,
                      scribe_word_count: (out.draft_text || '').split(/\s+/).length,
                    } : {}),
                    completed: true,
                  } : prev);
                } catch (_) { /* non-fatal */ }
                finalResultReceived = true;
                break;
              }
              if (res.status === 404) {
                // Session row not yet written by orchestrator — keep polling.
                // The MIN_POLL_FLOOR_MS guards against bailing too early
                // on the first 360 s of 404s.
                continue;
              }
              if (res.status === 401 || res.status === 403) {
                // Auth-terminal — bail; ReauthBanner will surface via apiFetch.
                lastErr = `HTTP ${res.status}`;
                break;
              }
              // 5xx (or any other transient) — keep polling. The backend
              // is the source of truth and may recover; we never declare
              // a review failed from a transient transport hiccup.
              lastErr = `HTTP ${res.status}`;
              continue;
            } catch (pollErr) {
              lastErr = pollErr?.message || String(pollErr);
              // Network blip — try again.
              continue;
            }
          }
          if (!finalResultReceived) {
            // Sprint 11.2 (2026-06-13) — polling deadline reached
            // without a terminal envelope. DO NOT mark this message as
            // a hard failure (`isError: true`) — the backend may still
            // be running and the envelope is persisted on completion.
            // We surface a neutral copy and preserve whatever was
            // already streamed into `content`. The user can refresh
            // later and the envelope-restore path will rehydrate.
            console.warn('[useAgentChat] polling fallback exhausted:', lastErr);
            const isArabic = language === 'ar';
            const stillRunningMsg = isArabic
              ? 'لا تزال المراجعة قيد المعالجة في الخلفية. حدّث الصفحة بعد قليل لعرض النتيجة.'
              : 'Your review is still processing in the background. Refresh the page in a moment to see the result.';
            setMessages((prev) => prev.map((m) =>
              m.id === assistantId
                ? { ...m,
                    isStreaming: false,
                    // intentionally NOT isError — review may still complete
                    meta: { ...(m.meta || {}), transportState: 'polling_exhausted', polling_last_err: lastErr },
                    content: m.content || stillRunningMsg }
                : m,
            ));
          }
        } catch (pollOuter) {
          console.error('[useAgentChat] polling fallback crashed:', pollOuter);
        }
      }

      setIsStreaming(false);
      setMessages((prev) => prev.map((m) =>
        m.id === assistantId ? { ...m, isStreaming: false } : m,
      ));
      abortRef.current = null;
    }
  }, [language, session?.access_token, sessionId, getEffectiveLanguage]);

  return {
    messages,
    reasoningSteps,
    sources,
    followUps,
    isStreaming,
    currentDecision,
    error,
    sessionId,
    sequencerState,
    sendMessage,
    stop,
    reset,
    loadEnvelope,
  };
};

// ── Event handler (kept outside the hook so it doesn't recreate) ────
function handleEvent(
  event, assistantId,
  setMessages, setReasoningSteps, setSources, setFollowUps, setCurrentDecision, setSessionId,
  setSequencerState,
) {
  const type = event.type;
  const data = event.data || {};

  switch (type) {
    case 'sequencer_start': {
      // Initialise the three parallel agent tracks. The agents named
      // by the backend are echoed verbatim so the UI can render any
      // future fan-out shape without code changes.
      const names = Array.isArray(data.agents) && data.agents.length
        ? data.agents
        : ['examiner', 'regis', 'counsel'];
      const agentsState = {};
      for (const n of names) {
        agentsState[n] = { status: 'running', elapsed_s: 0, summary: '' };
      }
      setSequencerState?.((prev) => ({
        active: true,
        agents: agentsState,
        conductor: { status: 'waiting', elapsed_s: 0 },
        // Progress Plan v2: the `plan` event may arrive before this
        // reset — carry it across instead of wiping it.
        plan: prev?.plan,
      }));
      break;
    }
    case 'plan': {
      // Progress Plan v2 (Jul 2026): the orchestrator announces its
      // full checklist upfront. Labels are case-specific real data
      // (doc size, conditional Sentinel with policy count) and refine
      // later via plan_update (frameworks, obligations count).
      setSequencerState?.((prev) => ({
        ...(prev || {
          active: true,
          agents: {},
          conductor: { status: 'waiting', elapsed_s: 0 },
        }),
        plan: {
          task: data.task,
          steps: (data.steps || []).map((s) => ({ ...s, details: [] })),
        },
      }));
      break;
    }
    case 'plan_update': {
      setSequencerState?.((prev) => {
        if (!prev?.plan) return prev;
        const steps = prev.plan.steps.map((s) => (s.id === data.id
          ? {
            ...s,
            ...(data.status ? { status: data.status } : {}),
            ...(data.label ? { label: data.label } : {}),
          }
          : s));
        return { ...prev, plan: { ...prev.plan, steps } };
      });
      break;
    }
    case 'sequencer_agent_complete': {
      const name = data.agent;
      if (!name) break;
      setSequencerState?.((prev) => {
        if (!prev) return prev;
        const agents = { ...(prev.agents || {}) };
        agents[name] = {
          ...(agents[name] || {}),
          status: data.success ? 'completed' : 'failed',
          elapsed_s: data.elapsed_s || agents[name]?.elapsed_s || 0,
        };
        let plan = prev.plan;
        if (plan?.steps?.some((s) => s.id === name)) {
          plan = {
            ...plan,
            steps: plan.steps.map((s) => (s.id === name
              ? { ...s, status: data.success ? 'done' : 'failed', elapsed_s: data.elapsed_s || s.elapsed_s }
              : s)),
          };
        }
        return { ...prev, agents, plan };
      });
      break;
    }
    case 'sequencer_agent_start': {
      // Sprint 4: Scribe → (Examiner + Regis) → Conductor needs
      // explicit start events because Scribe completes BEFORE the
      // other agents launch, so the initial `sequencer_start` fan-out
      // doesn't include them. Insert the row lazily when each agent's
      // start arrives.
      const name = data.agent;
      if (!name) break;
      setSequencerState?.((prev) => {
        if (!prev) return prev;
        const agents = { ...(prev.agents || {}) };
        agents[name] = {
          status: 'running',
          elapsed_s: 0,
          summary: data.message || '',
        };
        let plan = prev.plan;
        if (plan?.steps?.some((s) => s.id === name)) {
          plan = {
            ...plan,
            steps: plan.steps.map((s) => (s.id === name && s.status === 'pending'
              ? { ...s, status: 'active' }
              : s)),
          };
        }
        return { ...prev, agents, plan };
      });
      break;
    }
    case 'answer_token': {
      // Jul 2026 (UX-1): token-level streaming of research / Q&A
      // answers. Deltas are BUFFERED and flushed every ~120ms — a
      // setState per token (dozens/sec) saturated the main thread and
      // froze the renderer for the whole generation (observed in
      // production 2026-07-12: page could not paint for 30s+).
      // `final_result` still lands last with the complete text and
      // overwrites `content`, so a dropped delta can never corrupt
      // the final answer.
      const answerDelta = data.token || '';
      if (!answerDelta) break;
      let entry = TOKEN_FLUSH_BUFFERS.get(`ans:${assistantId}`);
      if (!entry) {
        entry = { buf: '', timer: null };
        TOKEN_FLUSH_BUFFERS.set(`ans:${assistantId}`, entry);
      }
      entry.buf += answerDelta;
      if (!entry.timer) {
        entry.timer = setTimeout(() => {
          const chunk = entry.buf;
          entry.buf = '';
          entry.timer = null;
          if (!chunk) return;
          setMessages((prev) => prev.map((m) =>
            m.id === assistantId
              ? { ...m, content: (m.content || '') + chunk, isStreaming: true }
              : m,
          ));
        }, 120);
      }
      break;
    }
    case 'scribe_section_token': {
      // Jul 2026 (live parallel typing): per-section token stream from
      // the 5 concurrent Scribe section calls. Buffered like the other
      // token streams; flushes into sequencerState.section_texts[key]
      // so the document panel renders all sections growing at once.
      const sDelta = data.token || '';
      const sKey = data.section;
      if (!sDelta || !sKey) break;
      const bufKey = `sect:${assistantId}:${sKey}`;
      let sEntry = TOKEN_FLUSH_BUFFERS.get(bufKey);
      if (!sEntry) {
        sEntry = { buf: '', timer: null };
        TOKEN_FLUSH_BUFFERS.set(bufKey, sEntry);
      }
      sEntry.buf += sDelta;
      if (!sEntry.timer) {
        sEntry.timer = setTimeout(() => {
          const chunk = sEntry.buf;
          sEntry.buf = '';
          sEntry.timer = null;
          if (!chunk) return;
          setSequencerState?.((prev) => prev ? {
            ...prev,
            section_texts: {
              ...(prev.section_texts || {}),
              [sKey]: ((prev.section_texts || {})[sKey] || '') + chunk,
            },
          } : prev);
        }, 150);
      }
      break;
    }
    case 'scribe_token': {
      // Sprint 4 Fix 2: token-level streaming into the Draft tab.
      // Jul 2026: same ~120ms buffered flush as answer_token — a
      // setState per token froze the renderer on long drafts (the
      // biggest documents froze the page the longest, which users
      // read as "drafting is broken"). `scribe_complete` still
      // carries the full text so a dropped delta never truncates.
      const delta = data.token || '';
      if (!delta) break;
      let entry = TOKEN_FLUSH_BUFFERS.get(`scribe:${assistantId}`);
      if (!entry) {
        entry = { buf: '', timer: null };
        TOKEN_FLUSH_BUFFERS.set(`scribe:${assistantId}`, entry);
      }
      entry.buf += delta;
      if (!entry.timer) {
        entry.timer = setTimeout(() => {
          const chunk = entry.buf;
          entry.buf = '';
          entry.timer = null;
          if (!chunk) return;
          setSequencerState?.((prev) => prev ? {
            ...prev,
            scribe_draft_text: (prev.scribe_draft_text || '') + chunk,
          } : prev);
        }, 120);
      }
      break;
    }
    case 'scribe_parallel_start': {
      // Sprint 4 (May-13 2026): parallel section drafting begins.
      // Initialise a per-section status map; the SequencerPanel renders
      // a 5-row checklist that ticks as each section completes.
      const sections = Array.isArray(data.sections) ? data.sections : [];
      const sectionMap = {};
      for (const s of sections) {
        const key = s.key || s;
        sectionMap[key] = {
          key,
          label_ar: s.label_ar || key,
          label_en: s.label_en || key,
          status: 'queued',
          word_count: 0,
          elapsed_s: 0,
        };
      }
      setSequencerState?.((prev) => prev ? {
        ...prev,
        draft_sections: sectionMap,
        draft_sections_order: sections.map((s) => s.key || s),
      } : prev);
      break;
    }
    case 'scribe_section_progress': {
      // Per-section status / word-count tick during parallel drafting.
      const key = data.section;
      if (!key) break;
      setSequencerState?.((prev) => prev ? {
        ...prev,
        draft_sections: {
          ...(prev.draft_sections || {}),
          [key]: {
            ...((prev.draft_sections || {})[key] || { key }),
            status: data.status || 'running',
            word_count: typeof data.word_count === 'number' ? data.word_count : ((prev.draft_sections || {})[key]?.word_count || 0),
            elapsed_s: typeof data.elapsed_s === 'number' ? data.elapsed_s : ((prev.draft_sections || {})[key]?.elapsed_s || 0),
            error: data.error || null,
          },
        },
      } : prev);
      break;
    }
    case 'scribe_complete': {
      // Sprint 4: Scribe finished and the draft text is ready. Stash
      // it on sequencerState so AgentPreviewPage's right pane can
      // render the Document panel's "Draft" tab immediately, without
      // waiting for Examiner/Regis/Conductor. The Review tab fills
      // in later via `conductor_complete` → final result envelope.
      //
      // Defensive (May-13 2026): also mark any still-running section in
      // `draft_sections` as completed. If a `scribe_section_progress`
      // event was dropped (rare — proxy buffering, polling fallback
      // taking over mid-stream, etc.), this guarantees the Sequencer's
      // per-section list is in sync with the top-level Scribe bar by
      // the time the user sees "Draft complete ✓".
      setSequencerState?.((prev) => {
        if (!prev) return prev;
        const next = {
          ...prev,
          scribe_draft_text: data.draft_text || '',
          scribe_word_count: data.word_count || 0,
        };
        if (prev.draft_sections) {
          const closed = {};
          for (const [k, s] of Object.entries(prev.draft_sections)) {
            closed[k] = (s && s.status === 'completed')
              ? s
              : { ...(s || { key: k }), status: 'completed' };
          }
          next.draft_sections = closed;
        }
        return next;
      });
      break;
    }
    case 'sequencer_agent_failed': {
      const name = data.agent;
      if (!name) break;
      setSequencerState?.((prev) => {
        if (!prev) return prev;
        const agents = { ...(prev.agents || {}) };
        agents[name] = {
          ...(agents[name] || {}),
          status: 'failed',
          elapsed_s: data.elapsed_s || agents[name]?.elapsed_s || 0,
          summary: data.error || agents[name]?.summary || '',
        };
        let plan = prev.plan;
        if (plan?.steps?.some((s) => s.id === name)) {
          plan = {
            ...plan,
            steps: plan.steps.map((s) => (s.id === name ? { ...s, status: 'failed' } : s)),
          };
        }
        return { ...prev, agents, plan };
      });
      break;
    }
    case 'sequencer_fan_out_complete': {
      // All three primary agents have settled. Conductor is about to start.
      setSequencerState?.((prev) => prev ? { ...prev, conductor: { ...(prev.conductor || {}), status: 'queued' } } : prev);
      break;
    }
    case 'conductor_start': {
      setSequencerState?.((prev) => prev ? {
        ...prev,
        conductor: { status: 'running', elapsed_s: 0 },
        plan: prev.plan ? {
          ...prev.plan,
          steps: prev.plan.steps.map((s) => (s.id === 'conductor' ? { ...s, status: 'active' } : s)),
        } : prev.plan,
      } : prev);
      break;
    }
    case 'conductor_complete': {
      setSequencerState?.((prev) => prev ? {
        ...prev,
        conductor: { status: data.success === false ? 'failed' : 'completed', elapsed_s: data.elapsed_s || 0 },
        plan: prev.plan ? {
          ...prev.plan,
          steps: prev.plan.steps.map((s) => (s.id === 'conductor'
            ? { ...s, status: data.success === false ? 'failed' : 'done', elapsed_s: data.elapsed_s || s.elapsed_s }
            : s)),
        } : prev.plan,
        // Mark the whole run as completed so AgentPreviewPage can keep
        // the SequencerPanel mounted after streaming ends (otherwise the
        // panel disappears the instant SSE closes — the user never sees
        // the final ✓ ✓ ✓ ✓ state). It resets to null when the user
        // sends the next message (see `setSequencerState(null)` at the
        // top of sendMessage).
        completed: true,
      } : prev);
      break;
    }
    case 'stream_start': {
      if (data.session_id) setSessionId(data.session_id);
      break;
    }
    case 'decision': {
      setCurrentDecision(data);
      setMessages((prev) => prev.map((m) =>
        m.id === assistantId
          ? { ...m, meta: { ...(m.meta || {}), agent: data.agent, task: data.task, classifier: data.classifier } }
          : m,
      ));
      break;
    }
    case 'reasoning_step': {
      const step = {
        type: 'reasoning_step',
        content: data.step || '',
        agent: data.agent,
        timestamp: event.timestamp,
      };
      setReasoningSteps((prev) => [...prev, step]);
      // Jul 2026 (mission-control strip): mirror steps into the
      // sequencer state so SequencerPanel's ticker, step counter, and
      // particle-field ripples fire on real agent events.
      setSequencerState?.((prev) => {
        if (!prev) return prev;
        const prevSteps = prev.last_steps || [];
        const lastEntry = prevSteps[prevSteps.length - 1];
        // Consecutive duplicates ("Finalizing your review…" ×N) do not
        // re-enter the ticker — they'd read as a stuck loop.
        const nextSteps = (lastEntry && lastEntry.content === (data.step || ''))
          ? prevSteps
          : [...prevSteps, { agent: data.agent, content: data.step || '' }].slice(-12);
        let plan = prev.plan;
        if (plan?.steps?.length && (data.step || '')) {
          const targetId = plan.steps.some((s) => s.id === data.agent)
            ? data.agent
            : (plan.steps.find((s) => s.status === 'active')?.id);
          if (targetId) {
            plan = {
              ...plan,
              steps: plan.steps.map((s) => {
                if (s.id !== targetId) return s;
                const d = s.details || [];
                if (d[d.length - 1] === data.step) return s;
                return { ...s, details: [...d, data.step].slice(-6) };
              }),
            };
          }
        }
        return {
          ...prev,
          steps_total: (prev.steps_total || 0) + 1,
          last_steps: nextSteps,
          event_seq: (prev.event_seq || 0) + 1,
          plan,
        };
      });
      setMessages((prev) => prev.map((m) =>
        m.id === assistantId
          ? { ...m, meta: { ...(m.meta || {}), reasoning_steps: [...(m.meta?.reasoning_steps || []), step] } }
          : m,
      ));
      break;
    }
    case 'source_found': {
      // Not rendered as a timeline line (the backend's humanized
      // reasoning_step events cover that — May-21 UX audit), but it
      // now feeds the mission-control strip: the "sources checked"
      // odometer climbs and the particle field ripples.
      setSequencerState?.((prev) => prev ? {
        ...prev,
        sources_checked: (prev.sources_checked || 0) + (Number(data.count) || 0),
        event_seq: (prev.event_seq || 0) + 1,
      } : prev);
      break;
    }
    case 'iteration_complete': {
      // Same reasoning as `source_found` above — the human-readable
      // narrative is already in the backend's reasoning_step stream.
      // `corpus 1 complete · 5 sources` would read as telemetry next
      // to "Refining search to find more relevant articles" so we
      // drop it from the visible timeline.
      break;
    }
    case 'final_result': {
      // Progress Plan v2: when the envelope lands, everything not
      // explicitly failed is finished — close the checklist so no
      // spinner outlives the run.
      setSequencerState?.((prev) => {
        if (!prev?.plan) return prev;
        return {
          ...prev,
          completed: true,
          plan: {
            ...prev.plan,
            steps: prev.plan.steps.map((s) => (s.status === 'failed' ? s : { ...s, status: 'done' })),
          },
        };
      });
      // Jul 2026: cancel any pending token-flush timers for this
      // message so a late buffered chunk can't append AFTER the
      // authoritative full text lands below.
      for (const key of Array.from(TOKEN_FLUSH_BUFFERS.keys())) {
        if (key.includes(assistantId)) {
          const entry = TOKEN_FLUSH_BUFFERS.get(key);
          if (entry && entry.timer) clearTimeout(entry.timer);
          TOKEN_FLUSH_BUFFERS.delete(key);
        }
      }
      const result = data.result || {};
      const output = result.output || {};
      // Sep 2026 (error-copy fix) — never mix Arabic + English in the
      // same bubble. handleEvent is module-level (no `language`
      // closure); infer from the envelope's own text so the fallback
      // notice matches the run's locale.
      const _outSample = (output.answer || output.draft_text || output.review_text || '').slice(0, 400);
      const isArabicLang = /[\u0600-\u06FF]/.test(_outSample);
      const emptyOutputMsg = isArabicLang
        ? 'لم يصل ناتج من المعالجة. إذا كنت تراجع مستنداً فتأكد من إرفاقه بنجاح ثم أعد المحاولة.'
        : 'No output was produced. If you attached a document, please re-attach it and try again.';
      const text = output.answer || output.draft_text || output.review_text
        || (output.gaps ? `Found ${output.gaps.length} gap(s)` : '')
        || (Object.keys(output).length ? JSON.stringify(output, null, 2) : emptyOutputMsg);
      const followUps = data.follow_ups || [];
      const finalSources = result.sources || [];

      setSources(finalSources);
      setFollowUps(followUps);
      if (data.session_id) setSessionId(data.session_id);

      setMessages((prev) => prev.map((m) =>
        m.id === assistantId
          ? {
              ...m,
              content: text,
              isStreaming: false,
              meta: {
                ...(m.meta || {}),
                sources: finalSources,
                follow_ups: followUps,
                output,
                agent: data.decision?.agent,
                task: data.decision?.task,
                // Bubble reads `meta.decision.task` to decide whether a
                // response is a (single-agent or full_review) review and
                // should render only the executive summary in-bubble.
                decision: data.decision || null,
              },
            }
          : m,
      ));
      // Defensive (May-13 2026): when `final_result` arrives via the
      // polling fallback (proxy cut the SSE stream before per-section
      // events landed), force-mark every `draft_sections` row as
      // completed. Without this the Sequencer stays stuck on
      // "running..." while the document panel already shows the full
      // draft.
      setSequencerState?.((prev) => {
        if (!prev || !prev.draft_sections) return prev;
        const closed = {};
        for (const [k, s] of Object.entries(prev.draft_sections)) {
          closed[k] = (s && s.status === 'completed')
            ? s
            : { ...(s || { key: k }), status: 'completed' };
        }
        return { ...prev, draft_sections: closed };
      });
      break;
    }
    case 'error': {
      setMessages((prev) => prev.map((m) =>
        m.id === assistantId
          ? { ...m, content: data.message || 'Agent error', isError: true, isStreaming: false }
          : m,
      ));
      break;
    }
    default:
      // Unknown event — ignore
      break;
  }
}

export default useAgentChat;
